import React, { useEffect, useMemo, useState } from "react";
import { Box, MenuItem, Select, Tab, Tabs, Typography } from "@mui/material";
import { isDarkMode } from "../../../utils/constants";
import { isMobileBrowser } from "../../../utils/user_agent";
import { dedupeTickerOptions } from "./utils";
import ChartCanvas from "./ChartCanvas";

const selectorLabelStyles = {
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: isDarkMode ? "#94a3b8" : "#64748b",
  mb: 0.5,
};

const menuPaperStyles = {
  mt: 1,
  borderRadius: "12px",
  border: `1px solid ${isDarkMode ? "#1f2937" : "#e5e7eb"}`,
  boxShadow: isDarkMode
    ? "0 12px 32px rgba(0,0,0,0.45)"
    : "0 12px 32px rgba(15,23,42,0.16)",
  background: isDarkMode ? "#0f1115" : "#ffffff",
  color: isDarkMode ? "#e5e7eb" : "#111827",
};

const buildSelectStyles = (isMobile, width) => ({
  width,
  minWidth: width,
  maxWidth: width,
  height: isMobile ? 36 : 38,
  borderRadius: "12px",
  background: isDarkMode
    ? "linear-gradient(180deg, #0f1115 0%, #0b0d10 100%)"
    : "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
  color: isDarkMode ? "#e5e7eb" : "#111827",
  fontSize: "13px",
  fontWeight: 700,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: isDarkMode ? "#1f2937" : "#e5e7eb",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: isDarkMode ? "#334155" : "#cbd5e1",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: isDarkMode ? "#7aa2ff" : "#2563eb",
  },
  "& .MuiSelect-select": {
    py: 0.75,
  },
});

const buildTabsStyles = (isMobile) => ({
  width: "100%",
  minWidth: "100%",
  boxSizing: "border-box",
  minHeight: 40,
  border: `1px solid ${isDarkMode ? "#1f2937" : "#e5e7eb"}`,
  borderRadius: "14px",
  px: 0.75,
  background: isDarkMode
    ? "linear-gradient(180deg, #0f1115 0%, #0b0d10 100%)"
    : "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
  "& .MuiTabs-flexContainer": {
    gap: isMobile ? 0.5 : 0.75,
    alignItems: "center",
  },
  "& .MuiTab-root": {
    textTransform: "none",
    minHeight: 32,
    minWidth: 76,
    margin: "4px 2px",
    padding: "6px 12px",
    borderRadius: "10px",
    color: isDarkMode ? "#d1d5db" : "#374151",
    fontWeight: 700,
    fontSize: "13px",
    letterSpacing: "0.01em",
    backgroundColor: "transparent",
    "&.Mui-selected": {
      color: isDarkMode ? "#7aa2ff" : "#2563eb",
      backgroundColor: isDarkMode
        ? "rgba(122,162,255,0.18)"
        : "rgba(37,99,235,0.15)",
    },
    "&:hover": {
      backgroundColor: isDarkMode
        ? "rgba(255,255,255,0.05)"
        : "rgba(15,23,42,0.06)",
    },
  },
  "& .MuiTabs-scrollButtons": {
    color: isDarkMode ? "#cbd5e1" : "#4b5563",
  },
  "& .MuiTabs-scrollButtons.Mui-disabled": {
    opacity: 0.35,
  },
});

const normalizeActions = (actions) =>
  (actions || []).filter(Boolean).map((action) => {
    const rawOptions = Array.isArray(action.options) ? action.options : [];
    const options = (
      action.dedupe ? dedupeTickerOptions(rawOptions) : rawOptions
    ).map((option) => ({
      ...option,
      value: option.value ?? option.id ?? option.label,
    }));
    return { ...action, options };
  });

const ChartActionDropdownTabs = ({
  actions = [],
  initialActionKey,
  dropdownLabel = "Chart Action",
  selectWidth,
  sx = {},
  showDropdown = true,
  showActionTabs = false,
  rightContent = null,
}) => {
  const isMobile = isMobileBrowser();
  const normalizedActions = useMemo(() => normalizeActions(actions), [actions]);
  const fallbackAction =
    normalizedActions.find((action) => (action.options || []).length > 0) ||
    normalizedActions[0] ||
    null;

  const [activeActionKey, setActiveActionKey] = useState(() => {
    if (
      initialActionKey &&
      normalizedActions.some((action) => action.key === initialActionKey)
    ) {
      return initialActionKey;
    }
    return fallbackAction?.key || "";
  });

  useEffect(() => {
    if (!normalizedActions.length) return;
    const hasCurrent = normalizedActions.some(
      (action) =>
        action.key === activeActionKey && (action.options || []).length > 0
    );
    if (!hasCurrent && fallbackAction?.key) {
      setActiveActionKey(fallbackAction.key);
    }
  }, [normalizedActions, activeActionKey, fallbackAction?.key]);

  const activeAction =
    normalizedActions.find((action) => action.key === activeActionKey) ||
    fallbackAction;
  const options = activeAction?.options || [];
  const hasOptions = options.length > 0;
  const tabValue =
    hasOptions && options.some((option) => option.value === activeAction?.value)
      ? activeAction?.value
      : hasOptions
        ? options[0]?.value
        : false;

  useEffect(() => {
    if (!hasOptions || activeAction?.value == null || !activeAction?.onChange) {
      return;
    }
    const matches = options.some((option) => option.value === activeAction.value);
    if (!matches && options[0]?.value != null) {
      activeAction.onChange(options[0].value);
    }
  }, [hasOptions, options, activeAction?.value, activeAction?.onChange]);

  if (!normalizedActions.length) return null;

  const width = selectWidth ?? "140px";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? 1 : 1.5,
        width: "100%",
        ...sx,
      }}
    >
      {showDropdown ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
            gap: isMobile ? 0.75 : 1.5,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexShrink: 0,
              minWidth: width,
            }}
          >
            {dropdownLabel ? (
              <Typography sx={selectorLabelStyles}>{dropdownLabel}</Typography>
            ) : null}
            <Select
              value={activeActionKey}
              onChange={(event) => setActiveActionKey(event.target.value)}
              sx={buildSelectStyles(isMobile, width)}
              MenuProps={{ PaperProps: { sx: menuPaperStyles } }}
            >
              {normalizedActions.map((action) => (
                <MenuItem
                  key={action.key}
                  value={action.key}
                  sx={{ fontSize: "13px" }}
                >
                  {action.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      ) : null}

      {showActionTabs ? (
        <Box
          sx={{
            display: "inline-flex",
            width: "fit-content",
            maxWidth: "100%",
            alignSelf: "flex-start",
          }}
        >
          <Tabs
            value={activeActionKey}
            onChange={(_event, newValue) => setActiveActionKey(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            TabIndicatorProps={{ sx: { display: "none" } }}
            sx={buildTabsStyles(isMobile)}
          >
            {normalizedActions.map((action) => (
              <Tab
                key={action.key}
                label={action.label}
                value={action.key}
                disabled={Boolean(action.disabled || !(action.options || []).length)}
              />
            ))}
          </Tabs>
        </Box>
      ) : null}

      {hasOptions ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 0.5 : 1,
            width: "100%",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Tabs
              value={tabValue}
              onChange={(_event, newValue) => {
                if (!activeAction || activeAction.disabled) return;
                if (typeof activeAction.onChange === "function") {
                  activeAction.onChange(newValue);
                }
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              TabIndicatorProps={{ sx: { display: "none" } }}
              sx={buildTabsStyles(isMobile)}
            >
              {options.map((option) => (
                <Tab
                  key={option.value ?? option.label}
                  label={option.label ?? option.value ?? ""}
                  value={option.value ?? option.label}
                  disabled={Boolean(activeAction?.disabled || option.disabled)}
                />
              ))}
            </Tabs>
          </Box>
          {rightContent}
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            border: `1px dashed ${isDarkMode ? "#374151" : "#d1d5db"}`,
            borderRadius: "12px",
            padding: "10px 12px",
            color: isDarkMode ? "#9ca3af" : "#6b7280",
            fontSize: "13px",
            backgroundColor: isDarkMode ? "rgba(255,255,255,0.02)" : "#fafafa",
          }}
        >
          No options available for this selection.
        </Box>
      )}
    </Box>
  );
};

export { ChartCanvas, ChartActionDropdownTabs };
